import { createTheme } from '@mui/material';
import { CSSProperties } from '@mui/material/styles/createMixins';

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    sm: false;
    md: false;
    lg: false;
    xs: true;
    xl: true;
  }
}

declare module '@mui/material/styles/createPalette' {
  interface CommonColors {
    [key: string]: string;
  }
}

declare module '@mui/material/styles/createMixins' {
  // Allow for custom mixins to be added
  interface Mixins {
    drawerFooter?: CSSProperties;
  }
}

declare module '@mui/material/styles/createTypography' {
  interface FontWeight {
    bold: number;
  }
}

export const colors = {
  white: '#FFFFFF',
  black: '#273138',
  gray: '#E6E8EC',
  gray1: '#D4DCE7',
  gray2: '#6B747A',
  gray3: '#F6F7F8',
  gray4: '#A1ABB1',
  gray5: '#EBEDEF',
  gray6: '#F4F4F6',
  gray7: '#C0C4DA',
  gray8: '#EFEFEF',
  gray9: '#F6F6FB',
  gray10: '#F7F7F7',
  gray11: '#BABABA',
  gray12: '#72768B',
  gray13: '#EBEBEB',
  gray14: '#DBDBDB',
  gray15: '#FAFAFA',
  gray16: '#E2E3E5',
  gray17: '#DADBDD',
  gray18: '#B6BABE',
  slate: '#9EA2BD',
  yellow: '#FFC554',
  orange: '#FFA621',
  orange2: '#FF8B42',
  lightOrange: '#FFECD0',
  lightOrange2: '#FDF5E5',
  red: '#FF655B',
  red2: '#D84446',
  lightRed: '#FFDCDA',
  lightRed2: '#FEE6E5',
  lightTurqoise: '#E3FFF5',
  lightTurqoise2: '#E0FCF2',
  green: '#42CB9A',
  lightGreen: '#EBFFD8',
  darkGreen: '#097B06',
  blue: '#4992FF',
  blue2: '#AED0DF',
  blue3: '#2A96D6',
  blue4: '#2FA3FF',
  blue5: '#0066B7',
  blue6: '#288BD9',
  blue7: '#4494F9',
  lightBlue: '#E0F5FE',
  lightBlue2: '#D4E5FF',
  lightBlue3: '#EEF0FC',
  lightBlue4: '#DFF5FD',
  lightBlue5: '#E6F2FF',
  lightBlue6: '#F5FAFF',
  purple: '#B598CA',
  lightPurple: '#E6E7FD',
  pink: '#FC9EFA',
  magenta: '#FF20B8',
  magentaOpacity5: '#FF20B80D',
  lightMagenta: '#FFF4FB',
  blackOpacity5: '#2731380D',
  lightPink: '#FEF0FE',
  red10: '#FEF1F0',
  red100: '#F0756D',
  orange10: '#FEF5E5',
  orange100: '#F59F00',
  attentionYellow: '#FFF9BF',
  green10: '#EBF8F3',
  green100: '#33B989',
  green200: '#D9F2E8',
  jetGrey: '#5e638b',
  lightGrey: '#6747A',
};

// Single source of truth for tooltip width across all apps. Tooltips wrap
// instead of stretching, so a hover hint never spans the screen.
export const TOOLTIP_MAX_WIDTH_PX = 400;

const theme = createTheme({
  palette: {
    common: colors,
    primary: {
      main: colors.blue4,
      dark: colors.blue6,
    },
    secondary: {
      main: colors.blue7,
    },
    warning: {
      main: colors.orange,
    },
    error: {
      main: colors.red2,
    },
    info: {
      main: colors.blue,
    },
    success: {
      main: colors.green100,
    },
  },

  typography: {
    fontFamily: "'Geist', sans-serif",
    fontWeightMedium: 500,
    fontWeightBold: 600,
  },

  components: {
    MuiTypography: {
      defaultProps: {
        color: colors.black,
      },
      styleOverrides: {
        root: {
          fontSize: '14px',
        },
      },
      variants: [
        {
          props: { variant: 'h3' },
          style: {
            fontWeight: 'bold',
            fontSize: '18px',
          },
        },
        {
          props: { variant: 'h4' },
          style: {
            fontWeight: 'bold',
            fontSize: '34px',
          },
        },
        {
          props: { variant: 'h5' },
          style: {
            fontWeight: 'bold',
            fontSize: '20px',
          },
        },
        {
          props: { variant: 'h6' },
          style: {
            fontWeight: 'bold',
            fontSize: '16px',
          },
        },
        {
          props: { variant: 'body1' },
          style: {
            fontWeight: 400,
            fontSize: '14px',
            color: colors.gray2,
          },
        },
        {
          props: { variant: 'caption' },
          style: {
            fontSize: '14px',
            color: `${colors.gray7} !important`,
            lineHeight: '1.5',
          },
        },
      ],
    },

    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },

    MuiFormControl: {
      styleOverrides: {
        root: {
          '.MuiInputLabel-root': {
            paddingRight: '4px',
            paddingTop: '1px',
            color: `${colors.gray7} !important`,
            fontSize: '14px',
          },
          '.MuiInputLabel-shrink': {
            fontSize: 'unset',
            paddingTop: 'unset',
          },
          '.Mui-focused': {
            color: `${colors.black} !important`,
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: `${colors.black}`,
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
          border: `1px solid ${colors.gray13}`,
          borderRadius: '8px',
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '8px',
          border: `1px solid ${colors.gray8}`,
        },
        root: {
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(39, 49, 56, 0.5)',
          },
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.gray16,
        },
      },
      variants: [
        {
          props: { light: true },
          style: {
            borderColor: colors.gray8,
          },
        },
      ],
    },

    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          border: `1px solid ${colors.gray8}`,
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
          borderRadius: '6px',
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: '8px !important',
        },
      },
    },

    MuiTooltip: {
      defaultProps: {
        placement: 'bottom-start',
      },
      styleOverrides: {
        tooltip: {
          background: `${colors.black}`,
          borderRadius: '8px',
          padding: '8px',
          fontSize: '12px',
          fontWeight: 400,
          // `width` and not `min-width`: a min-width of fit-content beats
          // max-width in CSS, which is what used to let tooltips grow past
          // the limit.
          width: 'fit-content',
          maxWidth: `${TOOLTIP_MAX_WIDTH_PX}px`,
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          alignItems: 'center',
        },
        standardSuccess: {
          backgroundColor: `${colors.green10}`,
          color: `${colors.black}`,
        },
        standardError: {
          backgroundColor: `${colors.red10}`,
          color: `${colors.black}`,
        },
        standardWarning: {
          backgroundColor: `${colors.orange10}`,
          color: `${colors.black}`,
        },
        filledWarning: {
          backgroundColor: `${colors.attentionYellow}`,
          color: `${colors.black}`,
        },
        standardInfo: {
          color: `${colors.black}`,
          backgroundColor: `${colors.gray9}`,
        },
      },
      defaultProps: { severity: 'info' },
    },
  },

  mixins: {
    drawerFooter: {
      height: '72px',
      minHeight: '72px',
    },
  },
});

export default theme;
